import { Activity, ChevronRight, Search } from "lucide-react";

const GROUP_LABELS = {
  operations: "Operations",
  infrastructure: "Infrastructure",
  controls: "Controls",
};

export const AdminSecondarySidebar = ({ activeId, badges, collapsed, items, onSelect, onToggle, query, setQuery, stats }) => {
  const filtered = items.filter((item) => item.label.toLowerCase().includes(query.trim().toLowerCase()));
  const groups = Object.keys(GROUP_LABELS)
    .map((group) => ({ id: group, label: GROUP_LABELS[group], items: filtered.filter((item) => item.group === group) }))
    .filter((group) => group.items.length);

  return (
    <aside
      className={`flex shrink-0 flex-col border-r border-slate-800/80 bg-[#0B1120]/80 backdrop-blur-sm transition-[width] duration-300 ${collapsed ? "w-16" : "w-16 md:w-72"}`}
      data-testid="secondary-sidebar"
    >
      {/* ===== Brand header ===== */}
      <div className="flex h-16 items-center justify-between border-b border-slate-800/80 px-3">
        {!collapsed && (
          <div className="hidden min-w-0 items-center gap-3 md:flex">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-cyan-500/30 bg-gradient-to-br from-cyan-500/20 to-indigo-500/10 shadow-inner">
              <Activity className="h-4 w-4 text-cyan-300" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-orbitron text-sm font-bold uppercase text-slate-100 tracking-wider">Admin Control</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Operations console</p>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-800 text-slate-400 transition-all duration-200 hover:border-cyan-500/40 hover:bg-cyan-500/10 hover:text-cyan-300"
          data-testid="collapse-sidebar"
          aria-label={collapsed ? "Expand admin navigation" : "Collapse admin navigation"}
          title={collapsed ? "Expand navigation" : "Collapse navigation"}
        >
          <ChevronRight className={`h-4 w-4 transition-transform duration-300 ${collapsed ? "" : "rotate-180"}`} />
        </button>
      </div>

      {/* ===== Search ===== */}
      {!collapsed && (
        <div className="hidden border-b border-slate-800/80 p-3 md:block">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find admin section"
              className="h-9 w-full rounded-lg border border-slate-800 bg-slate-950/60 pl-9 pr-3 text-xs text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-cyan-500/40 focus:bg-slate-950"
              data-testid="admin-nav-search"
            />
          </label>
        </div>
      )}

      {/* ===== Nav list ===== */}
      <nav className="flex-1 overflow-y-auto px-2 py-4" aria-label="Admin sections" data-testid="admin-section-navigation">
        {groups.map((group) => (
          <div key={group.id} className="mb-5 last:mb-0">
            {!collapsed && (
              <p className="mb-2 hidden px-3 wx-overline md:block">{group.label}</p>
            )}
            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = activeId === item.id;
                const badge = badges[item.id];
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(item.id)}
                    className={`group relative flex h-10 w-full items-center gap-3 rounded-lg border px-3 text-left text-sm transition-all duration-150 ${
                      active
                        ? "border-cyan-500/35 bg-gradient-to-r from-cyan-500/15 via-cyan-500/8 to-transparent text-cyan-50 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.03)]"
                        : "border-transparent text-slate-400 hover:border-slate-800 hover:bg-slate-900/50 hover:text-slate-100"
                    }`}
                    data-testid={`nav-${item.id}`}
                    aria-current={active ? "page" : undefined}
                    aria-label={item.label}
                    title={item.label}
                  >
                    <Icon className={`h-4 w-4 shrink-0 transition-colors ${active ? "text-cyan-300" : "text-slate-500 group-hover:text-slate-200"}`} />
                    {!collapsed && (
                      <span className="hidden min-w-0 flex-1 truncate font-medium md:block">{item.label}</span>
                    )}
                    {!collapsed && badge > 0 && (
                      <span
                        className="hidden min-w-5 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-center font-mono text-[10px] font-bold text-amber-300 md:block"
                        data-testid={`nav-${item.id}-badge`}
                      >
                        {badge}
                      </span>
                    )}
                    {active && <span className="absolute inset-y-2 left-0 w-0.5 rounded-r-full bg-cyan-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {!filtered.length && !collapsed && (
          <p className="hidden px-3 py-8 text-center text-xs text-slate-500 md:block" data-testid="admin-nav-empty">
            No section found
          </p>
        )}
      </nav>

      {/* ===== Footer stats ===== */}
      {!collapsed && (
        <div className="hidden border-t border-slate-800/80 p-3 md:block">
          <div className="wx-metric-group grid-cols-2">
            <div className="wx-metric">
              <p className="wx-metric-value" data-testid="admin-sidebar-users">{stats.totalUsers}</p>
              <p className="wx-metric-label">Users</p>
            </div>
            <div className="wx-metric">
              <p className="wx-metric-value text-emerald-300" data-testid="admin-sidebar-licensed">{stats.licensed}</p>
              <p className="wx-metric-label">Licensed</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};
